import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, CircularProgress, Chip, Stack, Tooltip, Link
} from '@mui/material';
import api from '../../config/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

const ROLE_LABEL = {
  on_hand: 'in stock',
  incoming: 'incoming PO',
  procure: 'buy',
  produce: 'make',
  cycle: 'recipe loop!',
  component: 'component',
};

/**
 * Live availability from the lead-time ENGINE (/api/lead-time) — the
 * stock-aware, parallel-components + sequential-production model (Vishal
 * 2026-07-06: "UI should call it"). Answers "how long until I can have QTY
 * units of this SKU", which is quantity-dependent — unlike the statistical
 * lead-time column on the list (median of PO history).
 *
 * Renders: qty input → days + date, the gating (critical) path, and the
 * engine's reorder plan (velocity-based ROP / safety stock / suggested qty).
 */
export default function LeadTimeAvailabilityPanel({ brandId, sku }) {
  const [qty, setQty] = useState('1000');
  const [avail, setAvail] = useState(null);
  const [reorder, setReorder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!brandId || !sku) return;
    // Reorder plan is qty-independent — fetch once per SKU.
    api.get(`${API_BASE}/api/lead-time/reorder`, { params: { brand_id: brandId, sku } })
      .then((r) => setReorder(r.data?.data || null))
      .catch(() => setReorder(null));
  }, [brandId, sku]);

  useEffect(() => {
    if (!brandId || !sku) return;
    const q = parseInt(qty, 10);
    if (!(q > 0)) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true); setError(null);
        const r = await api.get(`${API_BASE}/api/lead-time/availability`, {
          params: { brand_id: brandId, sku, qty: q }
        });
        setAvail(r.data?.data || null);
      } catch (e) {
        setError(e.response?.data?.error || 'Availability lookup failed');
        setAvail(null);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [brandId, sku, qty]);

  if (!brandId || !sku) return null;

  // Flatten one level of the critical path for a compact display: the
  // assembly row first, then its components (gating ones emphasized).
  const pathRows = [];
  for (const step of (avail?.critical_path || [])) {
    pathRows.push(step);
    // components appear as siblings in the payload, no extra flattening needed
  }

  const days = avail?.lead_time_days;

  return (
    <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Typography variant="subtitle2">Availability</Typography>
        <TextField
          size="small" label="Qty" type="number" value={qty}
          onChange={(e) => setQty(e.target.value)}
          inputProps={{ min: 1 }} sx={{ width: 120 }}
        />
        {loading ? <CircularProgress size={18} /> : avail && (
          <Tooltip title={avail.cyclic ? 'The recipe references itself — fix the BOM' : `Available on ${avail.available_date}`}>
            <Chip
              color={avail.cyclic ? 'error' : days === 0 ? 'success' : 'primary'}
              label={avail.cyclic ? 'recipe loop' : days === 0 ? 'In stock now' : `Ready in ${days} day${days === 1 ? '' : 's'} (${avail.available_date})`}
            />
          </Tooltip>
        )}
        {error && <Typography variant="caption" color="error.main">{error}</Typography>}
      </Stack>

      {avail && !avail.cyclic && days > 0 && pathRows.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {pathRows.map((s, i) => (
            <Typography key={`${s.sku}-${i}`} variant="caption" display="block"
              color={s.gating ? 'warning.main' : 'text.secondary'}
              sx={{ ml: s.role === 'component' ? 2 : 0, fontWeight: s.gating ? 600 : 400 }}>
              {s.gating ? '⚠ ' : ''}{s.sku} — {ROLE_LABEL[s.role] || s.role}
              {s.role === 'produce' && s.production_lead_time != null ? ` (+${s.production_lead_time}d production)` : ''}
              {s.role !== 'produce' ? ` · ready in ${s.ready}d` : ''}
              {s.gating ? ' — gating' : ''}
            </Typography>
          ))}
        </Box>
      )}

      {reorder && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Reorder plan: {Math.round(Number(reorder.avg_daily_demand) * 10) / 10}/day demand ·
          {' '}reorder at {Math.ceil(Number(reorder.reorder_point) || 0)} units
          {' '}(safety {Math.ceil(Number(reorder.safety_stock) || 0)}) ·
          {' '}on hand {Number(reorder.on_hand) || 0}
          {Number(reorder.order_qty) > 0 ? <> · <b>suggest ordering {reorder.order_qty}</b></> : ' · no order needed'}
        </Typography>
      )}
      <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
        Live calculation: uses current stock, incoming POs, then parallel component
        procurement + production time. Differs from the list's historical lead-time
        column, which is a statistic over past POs.{' '}
        <Link href="/inventory/stock" underline="hover">Stock levels ↗</Link>
      </Typography>
    </Box>
  );
}
